import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IdContactDto } from '@/services/ws-crm/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { Filter } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';
import { useTaskTablesContext } from '../../context';

export const AssignedContactFilter: FC = () => {
  const [t] = useTranslation(['backend', 'common']);
  const filters = useTaskTablesContext('filters$');
  const { updateFilter, assignedContactPrefix, setAssignedContactPrefix, assignedContactRequest } = useTaskTablesContext();

  const options = useSubscribe(
    assignedContactRequest.data$,
    (response) => response?.data.items.map((item) => ({ label: item.name, value: item.id })) ?? [],
  );
  const total = useSubscribe(assignedContactRequest.data$, (response) => response?.data.total ?? 0);
  const loading = useSubscribe(assignedContactRequest.pending$);

  const handleChange = useCallbackRef((option: Option<IdContactDto> | null) => {
    updateFilter('assignedContact', option);
  });

  const serverSideFiltering = useMemo(
    () => ({ filter: assignedContactPrefix, setFilter: setAssignedContactPrefix, total }),
    [assignedContactPrefix, setAssignedContactPrefix, total],
  );

  return (
    <Filter<IdContactDto>
      label={t('common:ASSIGNED_CONTACT')}
      options={options}
      selected={filters.assignedContact}
      onChange={handleChange}
      loading={loading}
      serverSideFiltering={serverSideFiltering}
      isClearable
      dataTestId='assigned-contact-filter'
    />
  );
};
